import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import { tokens } from "../../../../locales/tokens";
import { useDebounce } from "../../../../hooks/useDebounce";
import { selectPushNotifications } from "../../../../store/pushNotification/repository/selectors";
import { getPushNotifications } from "../../../../store/pushNotification/useCases/getPushNotifications/actions";
import { deletePushNotification } from "../../../../store/pushNotification/useCases/deleteNotification/actions";

type NotificationTab = "Ready" | "Completed" | "Deleted";

export const useNotificationList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabFromUrl = (searchParams.get("tab") as NotificationTab) || "Ready";
  const pageFromUrl = Number(searchParams.get("page")) || 1;
  const queryFromUrl = searchParams.get("query") || "";

  const [searchText, setSearchText] = useState(queryFromUrl);
  const [filters, updateFilters] = useState({
    page: pageFromUrl,
    rowsPerPage: 30,
    query: queryFromUrl,
    tab: tabFromUrl,
  });

  const { notifications, count, isLoading, errorsDeletePushNotifications } =
    useSelector(selectPushNotifications);

  const [pageAmount, setPageAmount] = useState(0);
  const [deleteClick, setDeleteClick] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const tabOptions = [
    { label: t(tokens.notification.readyForSending), value: "Ready" },
    { label: t(tokens.notification.sended), value: "Completed" },
    { label: t(tokens.notification.archive), value: "Deleted" },
  ];

  const combineRequestBody = useCallback(
    () => ({
      page: filters.page,
      perPage: filters.rowsPerPage,
      status: filters.tab,
      title: filters.query,
    }),
    [filters],
  );

  useEffect(() => {
    dispatch(getPushNotifications(combineRequestBody()));
  }, [dispatch, combineRequestBody]);

  useEffect(() => {
    if (count) {
      setPageAmount(Math.ceil(count / filters.rowsPerPage));
    }
  }, [count, filters.rowsPerPage]);

  useEffect(() => {
    if (!isLoading && deleteClick) {
      if (errorsDeletePushNotifications) {
        toast.error(errorsDeletePushNotifications);
      } else {
        filters.tab !== "Deleted"
          ? toast.success(t(tokens.notification.message.notificationRemoved))
          : toast.success(
              t(tokens.notification.message.notificationRemovedHard),
            );

        dispatch(getPushNotifications(combineRequestBody()));
      }
      setDeleteClick(false);
    }
  }, [
    deleteClick,
    isLoading,
    errorsDeletePushNotifications,
    filters.tab,
    dispatch,
    combineRequestBody,
    t,
  ]);

  const onSearchNotification = (value: string) => {
    const query = value || "";

    if (query.length !== 1) {
      updateFilters((prev) => ({ ...prev, query, page: 1 }));
      setSearchParams({
        page: "1",
        tab: filters.tab,
        query,
      });
      scrollTopList();
    }
  };

  const { debouncedWrapper: debouncedHandleNotificationSearch } = useDebounce(
    onSearchNotification,
    1000,
  );

  const handleSearchNotificationText = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    debouncedHandleNotificationSearch(e.target.value);
  };

  const handleTabsChange = (_: ChangeEvent<{}>, tab: string): void => {
    const typedTab = tab as NotificationTab;

    updateFilters((prev) => ({ ...prev, tab: typedTab, page: 1 }));
    setSearchParams({
      page: "1",
      tab: typedTab,
      query: filters.query,
    });
    scrollTopList();
  };

  const handlePageChange = useCallback(
    (_: ChangeEvent<unknown>, page: number): void => {
      updateFilters((prev) => ({ ...prev, page }));
      setSearchParams({
        page: String(page),
        tab: filters.tab,
        query: filters.query,
      });
      scrollTopList();
    },
    [filters.tab, filters.query, setSearchParams],
  );

  const handleDelete = (
    e: React.MouseEvent<HTMLButtonElement>,
    id: number,
    mode?: string,
  ) => {
    e.preventDefault();
    dispatch(deletePushNotification(mode ? { id, mode } : { id }));
    setDeleteClick(true);
  };

  const scrollTopList = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  };

  return {
    searchText,
    handleSearchNotificationText,
    filters,
    tabOptions,
    handleTabsChange,
    handlePageChange,
    handleDelete,
    pageAmount,
    notifications,
    scrollRef,
  };
};
